import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FeedbackOutlined as FeedbackIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    if (base.endsWith('/api')) base = base.slice(0, -4);
    return base;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '';
  }
  return 'http://localhost:3000';
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'error' },
  { value: 'reviewed', label: 'Reviewed', color: 'info' },
  { value: 'in_progress', label: 'In progress', color: 'warning' },
  { value: 'need_info', label: 'Need info', color: 'secondary' },
  { value: 'fixed', label: 'Fixed', color: 'success' },
  { value: 'wont_fix', label: "Won't fix", color: 'default' },
];

const emptyUpdate = { title: '', body: '', app_scope: 'both', is_active: true };

const TestersFeedback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const apiBase = getApiBaseUrl();
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [submissions, setSubmissions] = useState([]);
  const [appFilter, setAppFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState('new');
  const [editNotes, setEditNotes] = useState('');
  const [editReply, setEditReply] = useState('');
  const [savingSub, setSavingSub] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [savingQ, setSavingQ] = useState(false);

  const [updates, setUpdates] = useState([]);
  const [updateForm, setUpdateForm] = useState(emptyUpdate);
  const [savingU, setSavingU] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    const res = await axios.get(`${apiBase}/api/tester-feedback/submissions`, {
      headers: authHeaders,
      params: {
        app: appFilter,
        status: statusFilter,
        search: search || undefined,
        limit: 500,
      },
    });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
    setSubmissions(res.data.data || []);
  }, [apiBase, authHeaders, appFilter, statusFilter, search]);

  const fetchQuestions = useCallback(async () => {
    const res = await axios.get(`${apiBase}/api/tester-feedback/questions`, {
      headers: authHeaders,
    });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
    setQuestions(res.data.data || []);
  }, [apiBase, authHeaders]);

  const fetchUpdates = useCallback(async () => {
    const res = await axios.get(`${apiBase}/api/tester-feedback/updates`, {
      headers: authHeaders,
    });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
    setUpdates(res.data.data || []);
  }, [apiBase, authHeaders]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchSubmissions(), fetchQuestions(), fetchUpdates()]);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [fetchSubmissions, fetchQuestions, fetchUpdates]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const openSubmission = (row) => {
    setSelected(row);
    setEditStatus(row.status || 'new');
    setEditNotes(row.admin_notes || '');
    setEditReply(row.admin_reply || '');
  };

  const saveSubmission = async () => {
    if (!selected) return;
    setSavingSub(true);
    try {
      const res = await axios.patch(
        `${apiBase}/api/tester-feedback/submissions/${selected.id}`,
        {
          status: editStatus,
          admin_notes: editNotes,
          admin_reply: editReply,
        },
        { headers: authHeaders },
      );
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
      toast.success('Feedback updated');
      setSelected(null);
      await fetchSubmissions();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSavingSub(false);
    }
  };

  const saveQuestions = async () => {
    setSavingQ(true);
    try {
      const res = await axios.put(
        `${apiBase}/api/tester-feedback/questions`,
        { questions },
        { headers: authHeaders },
      );
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
      setQuestions(res.data.data || []);
      toast.success('Form questions saved — apps will load them next open');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSavingQ(false);
    }
  };

  const updateQuestionField = (index, path, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      const q = { ...next[index] };
      if (path.startsWith('labels.')) {
        const lang = path.split('.')[1];
        q.labels = { ...(q.labels || {}), [lang]: value };
      } else if (path.startsWith('detail_labels.')) {
        const lang = path.split('.')[1];
        q.detail_labels = { ...(q.detail_labels || {}), [lang]: value };
      } else {
        q[path] = value;
      }
      next[index] = q;
      return next;
    });
  };

  const createUpdate = async () => {
    if (!updateForm.title.trim() || !updateForm.body.trim()) {
      toast.error('Title and body required');
      return;
    }
    setSavingU(true);
    try {
      const res = await axios.post(
        `${apiBase}/api/tester-feedback/updates`,
        updateForm,
        { headers: authHeaders },
      );
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed');
      toast.success('Update posted to testers');
      setUpdateForm(emptyUpdate);
      await fetchUpdates();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSavingU(false);
    }
  };

  const toggleUpdateActive = async (row) => {
    try {
      await axios.patch(
        `${apiBase}/api/tester-feedback/updates/${row.id}`,
        { is_active: !row.is_active },
        { headers: authHeaders },
      );
      await fetchUpdates();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const deleteUpdate = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      await axios.delete(`${apiBase}/api/tester-feedback/updates/${id}`, {
        headers: authHeaders,
      });
      await fetchUpdates();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const statusChip = (status) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    return <Chip size="small" label={opt.label} color={opt.color} />;
  };

  const renderAnswers = (answers) => {
    if (!answers || typeof answers !== 'object') return '—';
    return Object.entries(answers).map(([key, val]) => {
      const yesNo = typeof val === 'object' ? val.answer : val;
      const detail = typeof val === 'object' ? val.detail : '';
      return (
        <Box key={key} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {key}
          </Typography>
          <Typography variant="body2">
            <strong>{String(yesNo || '—').toUpperCase()}</strong>
            {detail ? ` — ${detail}` : ''}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FeedbackIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Testers Feedback
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review Mitra &amp; Citizen tester reports · update form · post fixes to testers
            </Typography>
          </Box>
        </Stack>
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          onClick={refreshAll}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
        >
          <Tab label={`Submissions (${submissions.length})`} />
          <Tab label="Form questions" />
          <Tab label="Updates to testers" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && tab === 0 && (
        <Box>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            mb={2}
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>App</InputLabel>
              <Select
                label="App"
                value={appFilter}
                onChange={(e) => setAppFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="mitra">Mitra</MenuItem>
                <MenuItem value="citizen">Citizen</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="name, phone, area, notes…"
            />
          </Stack>

          {isMobile ? (
            <Stack spacing={1.5}>
              {submissions.map((row) => (
                <Paper key={row.id} sx={{ p: 1.5 }} onClick={() => openSubmission(row)}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Chip size="small" label={row.app_name} />
                    {statusChip(row.status)}
                  </Stack>
                  <Typography fontWeight={600}>
                    {row.user_name || row.user_phone || 'Anonymous'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.app_area || '—'} · {row.severity || '—'} ·{' '}
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString('en-IN')
                      : ''}
                  </Typography>
                  {row.free_text && (
                    <Typography variant="body2" mt={0.5} noWrap>
                      {row.free_text}
                    </Typography>
                  )}
                </Paper>
              ))}
              {!submissions.length && (
                <Alert severity="info">No feedback yet.</Alert>
              )}
            </Stack>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>App</TableCell>
                    <TableCell>Tester</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openSubmission(row)}
                    >
                      <TableCell>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString('en-IN')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.app_name} />
                      </TableCell>
                      <TableCell>
                        {row.user_name || row.user_phone || '—'}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.language}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.app_area || '—'}</TableCell>
                      <TableCell>{row.severity || '—'}</TableCell>
                      <TableCell>{statusChip(row.status)}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography noWrap variant="body2">
                          {row.free_text || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!submissions.length && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Alert severity="info">No feedback yet.</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {!loading && tab === 1 && (
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            mb={2}
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary">
              Yes/No questions with optional detail. Labels in English / Telugu / Hindi show in apps.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveQuestions}
              disabled={savingQ}
            >
              {savingQ ? 'Saving…' : 'Save form'}
            </Button>
          </Stack>
          <Stack spacing={2}>
            {questions.map((q, index) => (
              <Paper key={q.key || index} sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  mb={1.5}
                  alignItems={{ md: 'center' }}
                >
                  <Typography fontWeight={700} sx={{ minWidth: 160 }}>
                    {q.key}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Order"
                    value={q.sort_order}
                    onChange={(e) =>
                      updateQuestionField(index, 'sort_order', Number(e.target.value))
                    }
                    sx={{ width: 100 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Detail when</InputLabel>
                    <Select
                      label="Detail when"
                      value={q.detail_when || 'yes'}
                      onChange={(e) =>
                        updateQuestionField(index, 'detail_when', e.target.value)
                      }
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                      <MenuItem value="always">Always</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>App</InputLabel>
                    <Select
                      label="App"
                      value={q.app_scope || 'both'}
                      onChange={(e) =>
                        updateQuestionField(index, 'app_scope', e.target.value)
                      }
                    >
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="mitra">Mitra</MenuItem>
                      <MenuItem value="citizen">Citizen</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={q.is_active !== false}
                        onChange={(e) =>
                          updateQuestionField(index, 'is_active', e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </Stack>
                <Grid container spacing={1.5}>
                  {['en', 'te', 'hi'].map((lang) => (
                    <Grid item xs={12} md={4} key={`l-${lang}`}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Question (${lang})`}
                        value={(q.labels && q.labels[lang]) || ''}
                        onChange={(e) =>
                          updateQuestionField(index, `labels.${lang}`, e.target.value)
                        }
                        multiline
                      />
                    </Grid>
                  ))}
                  {['en', 'te', 'hi'].map((lang) => (
                    <Grid item xs={12} md={4} key={`d-${lang}`}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Detail hint (${lang})`}
                        value={(q.detail_labels && q.detail_labels[lang]) || ''}
                        onChange={(e) =>
                          updateQuestionField(
                            index,
                            `detail_labels.${lang}`,
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {!loading && tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={700} mb={1.5}>
                Post update to testers
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Shown inside the app feedback form (fixes, what to retest, thanks).
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Title"
                  size="small"
                  value={updateForm.title}
                  onChange={(e) =>
                    setUpdateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  fullWidth
                />
                <TextField
                  label="Message"
                  size="small"
                  value={updateForm.body}
                  onChange={(e) =>
                    setUpdateForm((p) => ({ ...p, body: e.target.value }))
                  }
                  fullWidth
                  multiline
                  minRows={4}
                />
                <FormControl size="small">
                  <InputLabel>App scope</InputLabel>
                  <Select
                    label="App scope"
                    value={updateForm.app_scope}
                    onChange={(e) =>
                      setUpdateForm((p) => ({ ...p, app_scope: e.target.value }))
                    }
                  >
                    <MenuItem value="both">Both apps</MenuItem>
                    <MenuItem value="mitra">Mitra only</MenuItem>
                    <MenuItem value="citizen">Citizen only</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={createUpdate}
                  disabled={savingU}
                >
                  {savingU ? 'Posting…' : 'Post update'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={1.5}>
              {updates.map((u) => (
                <Paper key={u.id} sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography fontWeight={700}>{u.title}</Typography>
                        <Chip size="small" label={u.app_scope} />
                        {!u.is_active && (
                          <Chip size="small" label="Hidden" color="default" />
                        )}
                      </Stack>
                      <Typography variant="body2" whiteSpace="pre-wrap">
                        {u.body}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleString('en-IN')
                          : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!u.is_active}
                            onChange={() => toggleUpdateActive(u)}
                          />
                        }
                        label=""
                      />
                      <IconButton size="small" onClick={() => deleteUpdate(u.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
              {!updates.length && (
                <Alert severity="info">No updates posted yet.</Alert>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>Feedback #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Meta
                </Typography>
                <Typography variant="body2">App: {selected.app_name}</Typography>
                <Typography variant="body2">
                  Area: {selected.app_area || '—'} · Severity: {selected.severity || '—'}
                </Typography>
                <Typography variant="body2">
                  Lang: {selected.language} · Version: {selected.app_version || '—'}
                </Typography>
                <Typography variant="body2">
                  Tester: {selected.user_name || '—'} {selected.user_phone || ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.device_info || ''}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Answers
                </Typography>
                {renderAnswers(selected.answers)}
                {selected.free_text && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2">Additional comments</Typography>
                    <Typography variant="body2" whiteSpace="pre-wrap">
                      {selected.free_text}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Internal admin notes"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Reply / update for tester (optional)"
                    value={editReply}
                    onChange={(e) => setEditReply(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    helperText="Saved on the submission for your team; use Updates tab for in-app broadcasts"
                  />
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={saveSubmission}
            disabled={savingSub}
          >
            {savingSub ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestersFeedback;
